import { CompactBand, BandTab, BandMintLink } from '@/components/CompactBand';
import type { ArBucket, ArTab } from '../types';
import { money, moneyRound } from '../utils';

interface Props {
  tab: ArTab;
  owed: number;
  claimCount: number;
  /** The "Past the payer's usual time" group count — how many are late. */
  lateCount: number;
  waitingCount: number;
  writtenOffCount: number;
  writtenOffTotal: number;
  buckets: ArBucket[];
  activeBucket: string | null;
  onBucket: (label: string) => void;
  tabTo: (tab: ArTab) => string;
  /** Route for the Follow-up settings link (Settings › Follow-up). */
  settingsTo: string;
}

/** '0–30 days' → '0–30', 'Over 120 days' → '120+'. */
function shortBucket(label: string): string {
  if (label === 'Over 120 days') return '120+';
  return label.replace(' days', '');
}

function headline(owed: number, claimCount: number, lateCount: number): React.ReactNode {
  if (claimCount === 0) {
    return 'Insurers don’t owe you anything right now.';
  }
  const lead = `Insurers owe you ${money(owed)} on ${String(claimCount)} claims.`;
  if (lateCount === 0) {
    return lead;
  }
  const late = lateCount === 1 ? 'One is late.' : `${String(lateCount)} are late.`;
  return (
    <>
      {lead} <em>{late}</em>
    </>
  );
}

export function Header({
  tab, owed, claimCount, lateCount, waitingCount, writtenOffCount, writtenOffTotal,
  buckets, activeBucket, onBucket, tabTo, settingsTo,
}: Props) {
  const agePills = (
    <div className="cbd-pills" role="group" aria-label="Filter by age">
      <span className="cbd-pills-label">Age, by date of service</span>
      {buckets.map((b) => (
        <button
          key={b.label}
          type="button"
          className="cbd-pill"
          aria-pressed={activeBucket === b.label}
          onClick={() => { onBucket(b.label); }}
        >
          <span className="l">{shortBucket(b.label)}</span>
          <span className="v">{moneyRound(b.amount)}</span>
        </button>
      ))}
    </div>
  );

  return (
    <CompactBand
      headline={headline(owed, claimCount, lateCount)}
      right={<BandMintLink to={settingsTo}>Follow-up settings</BandMintLink>}
      tabs={
        <>
          <BandTab
            label="Waiting for payment"
            meta={waitingCount}
            active={tab === 'waiting'}
            to={tabTo('waiting')}
          />
          <BandTab
            label="Written off"
            meta={`${String(writtenOffCount)} · ${moneyRound(writtenOffTotal)} this month`}
            active={tab === 'written-off'}
            to={tabTo('written-off')}
          />
        </>
      }
      tabsRight={tab === 'waiting' ? agePills : undefined}
    />
  );
}
